import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import config from "../../config";
import { AuthContext } from "../contexts/AuthContext";
import { useCreateReport } from "../data/Results/useCreateReport";
import { useGenerateAppendix } from "../data/Results/useGenerateAppendix";
import { useUpdateReportStatus } from "../data/Results/useUpdateReportStatus";
import { useGetReportingMeasurements } from "../data/useGetReportingMeasurements";
import { PagingConstants } from "../shared/constants/PagingConstants";
import { DateFilter } from "../shared/DateFilter";
import { RAGStatus, ReportStatuses } from "../shared/enums/Result";
import { DateTemplate } from "../shared/Grid/BodyTemplates";
import { getSubstationDisplayName } from "../shared/helpers/ResultUtils";
import { ReportValues, ReportingPayload } from "../shared/interfaces/ReportingPayload";
import { ReportStatusUpdate, Result, ResultWithReportInclusionStatus } from "../shared/interfaces/Result";
import { PagedData } from "../shared/interfaces/ResultsAPIInterfaces";
import { RAGStatusFilterDropdown } from "../shared/RAGStatusFilterDropdown";
import { ActionBodyTemplate } from "./ActionBodyTemplate";
import { HeaderToolbar } from "./HeaderToolbar";
import RAGStatusChip from "./RAGStatusChip";
import { ReportCheckbox } from "./ReportCheckbox";
import { ReportingMeasurementModal } from "./ReportingMeasurementModal";
import { ReportModalData } from "./ReportModalData";
import ReportStatusChip from "./ReportStatusChip";
import { ResultsGridAssetCellTemplate } from "./ResultsGridAssetCellTemplate";

interface Props {
  resultData?: PagedData<Result[]>;
  resultDataLoading: boolean;
  resultDataFetching: boolean;
  customerId: number;
  companyId: number;
  siteId: number;
  lazyState: DataTablePFSEvent;
  reportModalData: ReportModalData[];
  reportValuesList: ReportValues[];
  matchingReportModalData: ReportModalData[];
  matchingReportValuesList: ReportValues[];
  onFilter: (event: DataTablePFSEvent) => void;
  onSort: (event: DataTablePFSEvent) => void;
  onPage: (event: DataTablePFSEvent) => void;
  onDataRefreshClick: () => void;
  onUploadButtonClick: () => void;
  onGenerateAppendixSuccess: () => void;
  onGenerateAppendixError: () => void;
  setReportModalData: (reportModalData: ReportModalData[]) => void;
  setReportValuesList: (reportValuesList: ReportValues[]) => void;
  setMatchingReportModalData: (matchingReportModalData: ReportModalData[]) => void;
  setMatchingReportValuesList: (matchingReportValuesList: ReportValues[]) => void;
  refetchResults: () => void;
  handleUpdateReportStatusSuccess: () => void;
  handleUpdateReportStatusError: () => void;
}

export function LazyResultsGrid(props: Props) {
  const { t } = useTranslation();
  const isEaEmployee = useContext(AuthContext).amIEaEmployee();

  const topOfTableAnchorRef = useRef<HTMLDivElement>(null);

  const [showGenerateReport, setShowGenerateReport] = useState<boolean>(false);
  const generateAppendix = useGenerateAppendix({
    onSuccess: props.onGenerateAppendixSuccess,
    onError: props.onGenerateAppendixError,
  });
  const createReport = useCreateReport();

  const [tableData, setTableData] = useState<ResultWithReportInclusionStatus[] | undefined>();

  const [isCreateReportModalOpen, setIsCreateReportModalOpen] = useState<boolean>(false);

  const reportingMeasurements = useGetReportingMeasurements(
    props.customerId,
    props.resultData?.result.map(({ assetId, captureId }) => ({
      assetId: assetId,
      captureId,
    })),
  );

  useEffect(() => {
    setTableData(
      props.resultData?.result.map((result) => ({
        ...result,
        OverallRAGStatusIndex: Object.values(RAGStatus).indexOf(result.dataValue.OverallRAGStatus ?? RAGStatus.None),
        time: result.time,
        substationDisplayName: getSubstationDisplayName(result),
        report:
          props.reportValuesList.map(({ captureId }) => captureId).includes(result.captureId) ||
          props.matchingReportValuesList.map(({ captureId }) => captureId).includes(result.captureId)
            ? true
            : false,
      })),
    );
  }, [props.resultData?.result, props.reportValuesList, props.matchingReportValuesList]);

  function isTopOfTableInViewport(offset = 0) {
    if (!topOfTableAnchorRef?.current) return false;
    const top = topOfTableAnchorRef?.current.getBoundingClientRect().top;
    return top + offset >= 0 && top - offset <= window.innerHeight;
  }

  function handlePageChange(event: DataTablePFSEvent) {
    //scroll to the top of the table if the top isn't already visible (i.e. when using bottom paginator)
    if (!isTopOfTableInViewport()) topOfTableAnchorRef?.current?.scrollIntoView({ behavior: "smooth" });

    props.onPage(event);
  }

  function onGenerateAppendixButtonClick(): void {
    const reportingPayload = generateAppendixPayload();
    generateAppendix.mutate(reportingPayload);
  }

  function handleGenerateReportButtonClick(): void {
    setIsCreateReportModalOpen(true);
  }

  function onCreateReportButtonClick(description: string): void {
    const reportingPayload = generateReportingPayload(description);

    if (reportingPayload) {
      createReport.mutate(reportingPayload);
      setIsCreateReportModalOpen(false);
    }
  }

  const generateAppendixPayload = () => {
    const reportingPayload: ReportingPayload = {
      customerId: props.customerId,
      reportValuesList: props.reportValuesList.concat(props.matchingReportValuesList),
    };

    return reportingPayload;
  };

  const generateReportingPayload = (description: string) => {
    // If there are any report values we know we have at least one default report to include.
    if (props.reportValuesList.length > 0) {
      const reportingPayload: ReportingPayload = {
        customerId: props.customerId,
        // Check if any measurement checkboxes have been selected i.e. if there are any items in the matchingReportValuesList and if so include
        // these with the list of report values to include default reports and reports for specific measurements the user has selected.
        reportValuesList:
          props.matchingReportValuesList.length > 0
            ? props.reportValuesList.concat(props.matchingReportValuesList)
            : props.reportValuesList,
        description: description,
      };

      return reportingPayload;
    }
    // Else we do not have at least one default report so check if any measurement checkboxes have been selected and just include these.
    else if (props.matchingReportValuesList.length > 0) {
      const reportingPayload: ReportingPayload = {
        customerId: props.customerId,
        reportValuesList: props.matchingReportValuesList,
        description: description,
      };

      return reportingPayload;
    }
  };

  function handleCloseCreateReportModal(): void {
    setIsCreateReportModalOpen(false);
  }

  const headerToolbar = () => (
    <HeaderToolbar
      resultDataFetching={props.resultDataFetching}
      customerId={props.customerId}
      resultSelected={props.reportValuesList.length > 0 || props.matchingReportValuesList.length > 0}
      showGenerateReport={showGenerateReport}
      onDataRefreshClick={props.onDataRefreshClick}
      onUploadButtonClick={props.onUploadButtonClick}
      onGenerateAppendixButtonClick={onGenerateAppendixButtonClick}
      onGenerateReportButtonClick={handleGenerateReportButtonClick}
      isFlexibleSurvey={
        [...props.reportValuesList, ...props.matchingReportValuesList].some(
          (reportValue) =>
            props.resultData?.result.find((result) => result.captureId === reportValue.captureId)?.dataValue?.Assets,
        ) ?? false
      }
    />
  );

  const reportStatusCellTemplate = (rowData: Result) => {
    const rowStatus = rowData.status ?? ReportStatuses.NotViewed;
    return <ReportStatusChip status={rowStatus} />;
  };

  const ragStatusCellTemplate = (rowData: Result) => {
    const rowRagStatus = rowData.dataValue.OverallRAGStatus ?? RAGStatus.None;
    return <RAGStatusChip ragStatus={rowRagStatus} />;
  };

  const ragStatusFilterDropdown = (options: ColumnFilterElementTemplateOptions) => (
    <RAGStatusFilterDropdown options={options} />
  );

  const dateFilter = (options: ColumnFilterElementTemplateOptions) => (
    <DateFilter
      showButtonBar={true}
      options={options}
      placeholder={t("common.filter", { val: "$t(results.resultDate )" })}
    />
  );

  function handleCheckboxChange(captureId: number) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;

      updateTableData(captureId, isChecked);

      // If the check has been checked add the report value for that result.
      if (isChecked) {
        const selectedResult = tableData && tableData.find((row) => row.captureId === captureId);

        if (selectedResult !== undefined) {
          const reportValues = createReportValuesFromResult(selectedResult);

          const isMatching = isResultMatching(reportValues);

          if (isMatching) {
            reportValues.defaultReport = false;
            setMatchingModalValues(reportValues, selectedResult);
          } else {
            reportValues.defaultReport = true;
            setDefaultModalValues(reportValues, selectedResult);
          }
        }
      }
      // Else the checkbox has been unchecked so remove the report value for that result.
      else {
        removeReportValues(captureId);
      }
    };
  }

  function createReportValuesFromResult(result: ResultWithReportInclusionStatus): ReportValues {
    return {
      switchGearName: result.switchGearName,
      captureId: result.captureId,
      dataPointId: result.dataPointId,
      assetId: result.assetId,
      dataPointTypeId: result.dataPointTypeId,
      instrumentId: result.instrumentId,
      time: result.time,
      ragStatus: result.dataValue.OverallRAGStatus,
    };
  }

  function updateTableData(captureId: number, isChecked: boolean) {
    setTableData((tableData) =>
      tableData?.map((row) => ({
        ...row,
        report: row.captureId === captureId ? isChecked : row.report,
      })),
    );
  }

  function isResultMatching(reportValues: ReportValues): boolean {
    return (
      (reportingMeasurements.data &&
        reportingMeasurements.data.some((rm) => rm.resultCaptureId === reportValues.captureId)) ??
      false
    );
  }

  function setMatchingModalValues(reportValues: ReportValues, selectedResult: ResultWithReportInclusionStatus) {
    props.setMatchingReportValuesList([...props.matchingReportValuesList, reportValues]);

    const result = props.resultData?.result.find((result) => result.captureId === reportValues.captureId);

    const substationDisplayName = result && getSubstationDisplayName(result);

    const reportModalData: ReportModalData = {
      resultName: `${t("common.formattedDate", { dateString: selectedResult.time })} - ${substationDisplayName} - ${selectedResult.switchGearName}`,
      captureId: selectedResult.captureId,
    };
    props.setMatchingReportModalData([...props.matchingReportModalData, reportModalData]);
  }

  function setDefaultModalValues(reportValues: ReportValues, selectedResult: ResultWithReportInclusionStatus) {
    props.setReportValuesList([...props.reportValuesList, reportValues]);

    const result = props.resultData?.result.find((result) => result.captureId === reportValues.captureId);

    const substationDisplayName = result && getSubstationDisplayName(result);

    const reportModalData: ReportModalData = {
      resultName: `${t("common.formattedDate", { dateString: selectedResult?.time })} - ${substationDisplayName} - ${selectedResult?.switchGearName}`,
      captureId: selectedResult?.captureId,
    };

    props.setReportModalData([...props.reportModalData, reportModalData]);
  }

  function removeReportValues(captureId: number) {
    props.setReportValuesList(props.reportValuesList.filter((rvl) => rvl.captureId !== captureId));
    props.setReportModalData(props.reportModalData.filter((rmd) => rmd.captureId !== captureId));
    props.setMatchingReportValuesList(props.matchingReportValuesList.filter((rvl) => rvl.captureId !== captureId));
    props.setMatchingReportModalData(props.matchingReportModalData.filter((rmd) => rmd.captureId !== captureId));
  }

  const updateReportStatus = useUpdateReportStatus({
    onSuccess: handleUpdateReportStatusSuccess,
    onError: props.handleUpdateReportStatusError,
  });

  function onRemoveAnalysis(captureId: number, customerId: number, hvSubstationId?: number, hvSubstationName?: string) {
    const reportStatusUpdate: ReportStatusUpdate = {
      captureId: Number(captureId),
      status: ReportStatuses.Viewed,
      customerId: customerId,
      hvSubstationId: hvSubstationId,
      hvSubstationName: hvSubstationName,
    };
    updateReportStatus.mutate(reportStatusUpdate);
  }

  function handleUpdateReportStatusSuccess() {
    props.refetchResults();
    reportingMeasurements.refetch();
    props.handleUpdateReportStatusSuccess();
  }

  return (
    <>
      <div className="datatable-responsive" ref={topOfTableAnchorRef}>
        <DataTable
          value={tableData}
          header={headerToolbar}
          filters={props.lazyState.filters}
          filterDisplay="row"
          onFilter={props.onFilter}
          removableSort
          sortMode="multiple"
          className="p-datatable-responsive p-datatable-gridlines p-datatable-sm"
          emptyMessage={t("common.emptyMessage", {
            val: "$t(results.results )",
          })}
          loading={props.resultDataLoading}
          sortField={props.lazyState.sortField}
          sortOrder={props.lazyState.sortOrder}
          multiSortMeta={props.lazyState.multiSortMeta}
          onSort={props.onSort}
          // Paging functionality
          paginator={true}
          rowsPerPageOptions={[
            PagingConstants.TEN_ROW_AMOUNT,
            PagingConstants.INITIAL_ROW_AMOUNT,
            PagingConstants.FIFTY_ROW_AMOUNT,
            PagingConstants.HUNDRED_ROW_AMOUNT,
          ]}
          paginatorPosition="both"
          onPage={handlePageChange}
          first={props.lazyState.first}
          rows={props.lazyState.rows}
          totalRecords={props.resultData?.totalCount || 0}
          paginatorTemplate={
            "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          }
          currentPageReportTemplate={t("common.firstToLastRecords", {
            first: "{first}",
            last: "{last}",
            totalRecords: "{totalRecords}",
          })}
          lazy={true}
        >
          {config.reportsEnabled && isEaEmployee && (
            <Column
              field={"report"}
              header={t("results.report")}
              body={(rowData: ResultWithReportInclusionStatus) => (
                <ReportCheckbox
                  customerId={props.customerId}
                  substationId={rowData.dataValue.SubstationSummary?.SubstationId}
                  substationName={
                    rowData.dataValue.SubstationSummary?.SubstationName ??
                    rowData.dataValue.MetaData.SubstationGroup.SubstationName
                  }
                  captureId={rowData.captureId}
                  report={rowData.report}
                  showGenerateReport={showGenerateReport}
                  isLoading={reportingMeasurements.isLoading || reportingMeasurements.isFetching}
                  setShowGenerateReport={setShowGenerateReport}
                  onChange={handleCheckboxChange}
                />
              )}
              style={{ width: "3%" }}
            />
          )}
          {config.reportsEnabled && isEaEmployee && (
            <Column
              field={"status"}
              body={reportStatusCellTemplate}
              headerStyle={{ width: "10%", textAlign: "center" }}
              bodyStyle={{ textAlign: "center", overflow: "visible" }}
              header={t("results.Analysed")}
            />
          )}
          <Column
            field="dataValue.OverallRAGStatus"
            body={ragStatusCellTemplate}
            headerStyle={{ width: "10%", textAlign: "center" }}
            bodyStyle={{ textAlign: "center", overflow: "visible" }}
            header={t("results.overallStatus")}
            filter
            sortable
            filterElement={ragStatusFilterDropdown}
            showFilterMenu={false}
            showClearButton={false}
            sortField="OverallRAGStatusIndex"
          />
          <Column
            field={"time"}
            header={t("results.resultDate")}
            body={(rowData: ResultWithReportInclusionStatus) => (
              <DateTemplate rowData={rowData} field={"time"} header={t("results.resultDate")} />
            )}
            filter
            dataType="date"
            filterElement={dateFilter}
            showFilterMenu={false}
            showClearButton={false}
            sortable
            sortField="time"
            style={{ width: "15%" }}
          />
          <Column
            header={t("substations.substation")}
            field="substationDisplayName"
            filter
            filterPlaceholder="Substation Filter"
            showFilterMenu={false}
            showClearButton={false}
            sortable
            sortField="substationDisplayName"
            style={{ width: "27%" }}
          />
          <Column
            header={t("asset.assets")}
            body={ResultsGridAssetCellTemplate}
            field="switchGearName"
            style={{ width: "25%" }}
          />
          <Column
            body={(rowData: ResultWithReportInclusionStatus) => (
              <ActionBodyTemplate
                rowData={rowData}
                resultData={props.resultData}
                reportingMeasurements={reportingMeasurements.data}
                customerId={props.customerId}
                companyId={props.companyId}
                siteId={props.siteId}
                onRemoveAnalysis={onRemoveAnalysis}
              />
            )}
            headerStyle={{ width: "10%", textAlign: "center" }}
            bodyStyle={{ textAlign: "center", overflow: "visible" }}
            header={t("common.actions")}
          />
        </DataTable>
      </div>
      <div>
        <ReportingMeasurementModal
          isOpen={isCreateReportModalOpen}
          onClose={handleCloseCreateReportModal}
          reportValuesList={props.reportValuesList}
          reportModalData={props.reportModalData}
          matchingReportValuesList={props.matchingReportValuesList}
          matchingReportModalData={props.matchingReportModalData}
          onCreateReport={onCreateReportButtonClick}
        />
      </div>
    </>
  );
}
